import cx from 'classnames';
import React, {useCallback, useState, useEffect, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import type {
    FloatRootOverflow,
    AlignWithFloatRootTrigger,
} from '@pexip/components';
import {Draggable} from '@pexip/components';

import {
    desktopToolbarWidth,
    inMeetingDraggableInset,
    interfacesMargin,
    sidePanelPlusDesktopToolbarWidth,
} from '../../constants';
import {
    isControlsOverlapTruthy,
    isOverlappingSidePanel,
    useControlsRelativePosition,
    useSidePanelTransform,
} from '../../hooks';
import {getLangDirection} from '../../utils/getLangDirection';

import styles from './InMeetingDraggable.module.scss';

export interface InMeetingDraggablePosition {
    floatRootOverflow: FloatRootOverflow;
}

export const InMeetingDraggable = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        draggableAriaLabel: string;
        isMoveableWithArrowKeys?: boolean;
        showFocus?: boolean;
        isSidePanelVisible: boolean;
        isSidePanelTransformEnabled?: boolean;
        isDisabled?: boolean;
        alwaysAllowStyles?: boolean;
        floatRoot?: React.RefObject<HTMLDivElement | null>;
        center?: boolean;
        isPhone?: boolean;
        beforeRepositionSideEffect?: () => void;
        onRepositionSideEffect?: (
            el: HTMLDivElement,
            position: InMeetingDraggablePosition,
        ) => void;
        shouldCaptureClick?: boolean;
        alignWithFloatRootTrigger?: AlignWithFloatRootTrigger;
        pointerCapture?: boolean;
        pointerCaptureTarget?: HTMLElement | null;
        style?: React.CSSProperties;
        testId?: string;
    }
>(
    (
        {
            draggableAriaLabel = '',
            center = false,
            isMoveableWithArrowKeys,
            isSidePanelVisible,
            isSidePanelTransformEnabled = true,
            isDisabled,
            alwaysAllowStyles = false,
            floatRoot,
            isPhone,
            className,
            beforeRepositionSideEffect,
            onRepositionSideEffect,
            shouldCaptureClick = true,
            showFocus = true,
            alignWithFloatRootTrigger,
            pointerCapture,
            pointerCaptureTarget,
            children,
            style,
            testId,
            ...props
        },
        ref,
    ) => {
        let componentRef = useRef<HTMLDivElement>(null);
        componentRef =
            (ref as React.RefObject<HTMLDivElement | null>) ?? componentRef;

        const {i18n} = useTranslation();
        const dir = getLangDirection(i18n);

        const [initiallyCentered, setCenterInitially] = useState(false);
        const [isDraggableControlled, setIsDraggableControlled] =
            useState(false);

        const {controlsOverlap, update: updateControlsRelativePosition} =
            useControlsRelativePosition(componentRef);

        const setNoSidePanelLeft = useSidePanelTransform(
            isSidePanelVisible,
            componentRef,
            isSidePanelTransformEnabled,
        );

        const onPositionChange = useCallback(
            (el: HTMLDivElement) => {
                updateControlsRelativePosition();
                const elementRect = el.getBoundingClientRect();
                if (isOverlappingSidePanel(elementRect, dir)) {
                    setNoSidePanelLeft(interfacesMargin + desktopToolbarWidth);
                } else {
                    setNoSidePanelLeft(undefined);
                }
            },
            [updateControlsRelativePosition, setNoSidePanelLeft, dir],
        );

        const beforeReposition = useCallback(() => {
            if (componentRef.current) {
                componentRef.current.style.removeProperty('transform');
                componentRef.current.style.removeProperty('translate');
            }
            setIsDraggableControlled(true);
            if (beforeRepositionSideEffect) {
                beforeRepositionSideEffect();
            }
        }, [beforeRepositionSideEffect, componentRef.current]);

        const onReposition = useCallback(
            (el: HTMLDivElement, floatRootOverflow: FloatRootOverflow) => {
                onPositionChange(el);

                // don't overlap toolbar when side panel is open
                let offset = el.offsetLeft;
                if (dir === 'rtl') {
                    offset = window.innerWidth - el.offsetLeft - el.offsetWidth;
                }
                if (
                    isSidePanelVisible &&
                    offset < sidePanelPlusDesktopToolbarWidth
                ) {
                    if (dir === 'ltr') {
                        el.style.left = `${
                            (sidePanelPlusDesktopToolbarWidth /
                                window.innerWidth) *
                            100
                        }%`;
                    } else {
                        el.style.left = `calc(${
                            ((window.innerWidth -
                                sidePanelPlusDesktopToolbarWidth) /
                                window.innerWidth) *
                            100
                        }% - ${el.offsetWidth}px)`;
                        el.style.right = 'auto';
                    }
                }
                if (onRepositionSideEffect) {
                    onRepositionSideEffect(el, {floatRootOverflow});
                }
            },
            [onPositionChange, onRepositionSideEffect, isSidePanelVisible, dir],
        );

        useEffect(() => {
            const element = componentRef.current;
            if (element) {
                if (controlsOverlap.header || controlsOverlap.footer) {
                    element.style.removeProperty('top');
                    element.style.removeProperty('bottom');
                }
                if (controlsOverlap.desktopToolbar) {
                    if (dir === 'ltr') {
                        element.style.removeProperty('left');
                    } else {
                        element.style.right = `${desktopToolbarWidth + interfacesMargin}px`;
                    }
                }
            }
        }, [controlsOverlap, dir, componentRef.current]);

        useEffect(() => {
            if (isDraggableControlled && componentRef.current) {
                const el = componentRef.current;

                el.style.removeProperty('transition');
                el.ontransitionend = event => {
                    // we should ideally not leave inline translate after the animation
                    // as it blocks class styles and doesn't behave well with window resizing
                    if (event.propertyName !== 'translate') {
                        return;
                    }
                    let animationFrameTS: number;

                    requestAnimationFrame(ts => {
                        animationFrameTS = ts;
                        el.style.transition = 'none';
                        if (dir === 'ltr') {
                            el.style.left = `${(el.getBoundingClientRect().left / window.innerWidth) * 100}%`;
                        } else {
                            if (isSidePanelVisible) {
                                el.style.right = `${(sidePanelPlusDesktopToolbarWidth / window.innerWidth) * 100}%`;
                            } else {
                                el.style.right = `${(desktopToolbarWidth / window.innerWidth) * 100}%`;
                            }
                        }
                        el.style.removeProperty('translate');
                        // Tell the useSidePanelTransform hook the nudge was baked into left/right,
                        // so on close it uses the delta calc instead of translate(0,0)
                        if (isSidePanelVisible) {
                            setNoSidePanelLeft(
                                interfacesMargin + desktopToolbarWidth,
                            );
                        }
                    });

                    const removeInlineTransition = () => {
                        requestAnimationFrame(ts => {
                            if (ts <= animationFrameTS) {
                                // make sure this runs after the requested frame that sets transition to 'none'
                                removeInlineTransition();
                            } else {
                                updateControlsRelativePosition();
                                el.style.removeProperty('transition');
                            }
                        });
                    };
                    removeInlineTransition();

                    el.ontransitionend = null;
                };
            }
        }, [
            isDraggableControlled,
            isSidePanelVisible,
            updateControlsRelativePosition,
            setNoSidePanelLeft,
            dir,
            componentRef.current,
        ]);

        useEffect(() => {
            if (!isDisabled) {
                updateControlsRelativePosition();
                setCenterInitially(false);
            } else {
                updateControlsRelativePosition({
                    desktopToolbar: false,
                    footer: false,
                    header: false,
                });
                setCenterInitially(true);
            }
        }, [isDisabled, updateControlsRelativePosition]);

        const enableStyle = (shouldEnable: boolean | undefined) =>
            (alwaysAllowStyles || !isDisabled) && shouldEnable;

        return (
            <Draggable
                ariaLabel={draggableAriaLabel}
                testId={testId}
                isMoveableWithArrowKeys={isMoveableWithArrowKeys}
                isDisabled={isDisabled}
                showFocus={showFocus}
                floatRoot={floatRoot}
                onReposition={onReposition}
                beforeReposition={beforeReposition}
                shouldCaptureClick={shouldCaptureClick}
                ref={componentRef}
                pointerCapture={pointerCapture}
                pointerCaptureTarget={pointerCaptureTarget}
                alignWithFloatRootTrigger={alignWithFloatRootTrigger}
                floatRootInset={inMeetingDraggableInset}
                className={cx(
                    {
                        [styles.inMeetingDraggable]: enableStyle(true),
                        [styles.overlapsControls]: enableStyle(
                            isControlsOverlapTruthy(controlsOverlap),
                        ),
                        [styles.desktopToolbar]: enableStyle(
                            controlsOverlap.desktopToolbar,
                        ),
                        [styles.footer]: enableStyle(controlsOverlap.footer),
                        [styles.header]: enableStyle(controlsOverlap.header),
                        [styles.center]: enableStyle(
                            center &&
                                !isDraggableControlled &&
                                !initiallyCentered,
                        ),
                        [styles.centerInitial]: enableStyle(
                            center &&
                                !isDraggableControlled &&
                                initiallyCentered,
                        ),
                        [styles.sidePanelOpen]: enableStyle(isSidePanelVisible),
                        [styles.phone]: enableStyle(isPhone),
                    },
                    className,
                )}
                style={style}
                {...props}
            >
                {children}
            </Draggable>
        );
    },
);

InMeetingDraggable.displayName = 'InMeetingDraggable';
export type InMeetingDraggableProps = React.ComponentProps<
    typeof InMeetingDraggable
>;
